import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { MessageSquare, Lightbulb, Code2, Rocket, ArrowRight } from 'lucide-react'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { Button } from '@/components/ui/Button'
import { scrollToSection } from '@/lib/utils'

const steps = [
  {
    id: 'discovery',
    icon: MessageSquare,
    number: '01',
  },
  {
    id: 'planning',
    icon: Lightbulb,
    number: '02',
  },
  {
    id: 'development',
    icon: Code2,
    number: '03',
  },
  {
    id: 'launch',
    icon: Rocket,
    number: '04',
  },
]

export function Process() {
  const { t } = useTranslation()
  const { ref: sectionRef, isVisible } = useScrollAnimation<HTMLElement>()

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  }

  return (
    <section
      id="process"
      ref={sectionRef}
      className="py-16 sm:py-20 lg:py-32 bg-background relative overflow-hidden"
      aria-labelledby="process-heading"
    >
      {/* Background decoration */}
      <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent hidden sm:block" />

      <div className="container mx-auto px-5 sm:px-6 lg:px-8 relative">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isVisible ? 'visible' : 'hidden'}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-10 sm:mb-16">
            <span className="text-primary font-medium text-base sm:text-lg">{t('process.subtitle')}</span>
            <h2
              id="process-heading"
              className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-2 mb-4 sm:mb-6"
            >
              {t('process.title')}
            </h2>
            <p className="text-foreground-secondary text-base sm:text-lg max-w-2xl mx-auto leading-relaxed px-2">
              {t('process.description')}
            </p>
          </motion.div>

          {/* Process Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto mb-10 sm:mb-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                variants={itemVariants}
                custom={index}
                className="relative"
              >
                {/* Connector Line (hidden on mobile and last item) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-primary/50 to-primary/10 z-0" />
                )}

                <motion.div
                  className="relative bg-background-secondary rounded-2xl p-5 sm:p-6 border border-border hover:border-primary/30 transition-all duration-300 h-full"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  {/* Step Number */}
                  <div className="absolute -top-2.5 -right-2.5 sm:-top-3 sm:-right-3 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold shadow-lg">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <motion.div
                    className="p-3 sm:p-4 rounded-xl bg-primary/10 text-primary w-fit mb-3 sm:mb-4"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <step.icon className="w-6 h-6 sm:w-8 sm:h-8" aria-hidden="true" />
                  </motion.div>

                  {/* Content */}
                  <h3 className="text-base sm:text-lg font-bold mb-2">
                    {t(`process.steps.${step.id}.title`)}
                  </h3>
                  <p className="text-foreground-secondary text-sm sm:text-base leading-relaxed">
                    {t(`process.steps.${step.id}.description`)}
                  </p>

                  {/* Duration Badge */}
                  <div className="mt-3 sm:mt-4 inline-flex items-center gap-1 px-3 py-1.5 bg-primary/5 rounded-full text-xs sm:text-sm text-primary font-medium">
                    {t(`process.steps.${step.id}.duration`)}
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div variants={itemVariants} className="text-center px-4 sm:px-0">
            <p className="text-foreground-secondary text-sm sm:text-base mb-4">
              {t('process.cta.text')}
            </p>
            <Button
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="shadow-lg shadow-primary/25 group w-full sm:w-auto"
            >
              {t('process.cta.button')}
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}
